/**
 * Colonnes de la liste des campagnes d'UNE organisation (portail admin).
 *
 * Écrites contre la projection de `listerCampagnesOrgVue`. Pas de colonne
 * « Organisation » : elle est dans l'en-tête de page, la répéter sur chaque
 * ligne ne dirait rien de plus.
 */
import {
  colonneApplications,
  colonneListeFiltrable,
  FACETTE_APPLICATIONS,
  FACETTE_TEMPLATE,
} from "@repo/campagne";
import type { ApplicationEnum } from "@repo/utils";
import {
  Badge,
  Button,
  Progress,
  StatutCampagneBadge,
  filtreFacette,
  filtreRecherche,
  triOrdonne,
  type ColonneDataTable,
  type FacetteFiltre,
  type SortFnColonne,
} from "@repo/ui";
import { dateCourte } from "@repo/utils";
import { Archive, ArchiveRestore, Trash2, Users } from "lucide-react";
import type { Id } from "@convex/_generated/dataModel";

/**
 * Valeurs de la colonne « Archivage ». Une colonne dédiée plutôt qu'un 4ᵉ
 * statut : une campagne archivée reste « terminée » (ou « brouillon »), et les
 * deux informations doivent rester lisibles séparément.
 */
export const ETAT_ARCHIVE = {
  ACTIVES: "actives",
  ARCHIVEES: "archivees",
} as const;

/** Ordre métier du statut : cycle de vie, pas alphabet. */
const ORDRE_STATUT = ["brouillon", "active", "terminee"] as const;

const LIBELLES_STATUT: Record<string, string> = {
  brouillon: "Brouillon",
  active: "Active",
  terminee: "Terminée",
};

/**
 * Sentinelle du groupe absent.
 *
 * « Pas de groupe » est une valeur de filtre à part entière (les campagnes de
 * l'org qui ne sont rattachées à personne), mais la `FilterBar` écarte les
 * valeurs vides — sans quoi toute colonne facultative proposerait une case sans
 * libellé. Une sentinelle explicite lui redonne un nom, donc une case à cocher.
 */
export const SANS_GROUPE = "__sans_groupe__";

export const FACETTES_CAMPAGNES_ORG: FacetteFiltre[] = [
  {
    colonne: "statut",
    libelle: "Statut",
    libelles: LIBELLES_STATUT,
    ordre: ORDRE_STATUT,
  },
  FACETTE_APPLICATIONS,
  {
    colonne: "groupe",
    libelle: "Groupe",
    libelles: { [SANS_GROUPE]: "Sans groupe" },
  },
  FACETTE_TEMPLATE,
  {
    colonne: "archive",
    libelle: "Archivage",
    libelles: {
      [ETAT_ARCHIVE.ACTIVES]: "Actives",
      [ETAT_ARCHIVE.ARCHIVEES]: "Archivées",
    },
    ordre: [ETAT_ARCHIVE.ACTIVES, ETAT_ARCHIVE.ARCHIVEES],
  },
];

/** Recherche de la liste des campagnes d'une organisation. */
export const RECHERCHE_CAMPAGNES_ORG = {
  colonne: "nom",
  placeholder: "Nom de campagne…",
  libelle: "Rechercher une campagne",
} as const;

/** Projection retournée par `listerCampagnesOrgVue`. */
export type CampagneOrgLigne = {
  _id: Id<"campagnes">;
  nom: string;
  applications: ApplicationEnum[];
  statut: string;
  dateCreation: number;
  /** Présent = campagne archivée (timestamp d'archivage). */
  archivedAt?: number;
  organisationNom: string | null;
  groupeNom: string | null;
  nbCandidats: number;
  progression: {
    terminees: number;
    total: number;
    pourcentage: number;
  };
  templates: string[];
};

/** Coupe les mots composés sans casser la mise en page (`_` et `-`). */
function TemplatesCellule({ templates }: { templates: string[] }) {
  const texte = templates.join(", ");
  if (!texte) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="text-sm">
      {texte.split(/([_-])/).map((part, i) =>
        part === "_" || part === "-" ? (
          <span key={i}>
            {part}
            <wbr />
          </span>
        ) : (
          part
        ),
      )}
    </span>
  );
}

/** Tri alphabétique des groupes, les campagnes sans groupe rejetées à la fin. */
const triSansGroupeEnDernier: SortFnColonne<CampagneOrgLigne> = (a, b, id) => {
  const nom = (valeur: unknown) => (valeur === SANS_GROUPE ? "" : String(valeur));
  const gauche = nom(a.getValue(id));
  const droite = nom(b.getValue(id));
  if (gauche === droite) return 0;
  // Chaîne vide = sans groupe : toujours après, dans les deux sens de tri
  // TanStack inverse le comparateur, donc l'ordre relatif reste cohérent.
  if (gauche === "") return 1;
  if (droite === "") return -1;
  return gauche.localeCompare(droite, "fr");
};

/** Actions de ligne. Absentes = tableau en lecture seule. */
export interface ActionsCampagneOrg {
  onArchiver: (ligne: CampagneOrgLigne) => void;
  onDesarchiver: (ligne: CampagneOrgLigne) => void;
  onSupprimer: (ligne: CampagneOrgLigne) => void;
}

export function colonnesCampagnesOrg(
  actions?: ActionsCampagneOrg,
): ColonneDataTable<CampagneOrgLigne>[] {
  return [
    {
      id: "nom",
      header: "Nom",
      accessorFn: (c) => c.nom,
      cell: ({ row }) => (
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`font-medium truncate ${row.original.archivedAt ? "text-muted-foreground" : ""}`}
          >
            {row.original.nom}
          </span>
          {row.original.archivedAt && (
            <Badge variant="outline" className="shrink-0 text-xs">
              Archivée
            </Badge>
          )}
        </div>
      ),
      enableHiding: false,
      filterFn: filtreRecherche<CampagneOrgLigne>((c) => [c.nom]),
      meta: { libelle: "Nom", largeur: "24%" },
    },
    colonneApplications<CampagneOrgLigne>((c) => c.applications, "14%"),
    {
      id: "groupe",
      header: "Groupe",
      // La sentinelle passe par l'accessor : elle alimente la facette et son
      // compteur comme n'importe quelle autre valeur. L'affichage, lui, lit
      // `row.original` et reste un tiret.
      accessorFn: (c) => c.groupeNom ?? SANS_GROUPE,
      filterFn: filtreFacette<CampagneOrgLigne>(),
      // Les sans-groupe en dernier : sinon la sentinelle les trierait par son
      // propre texte, avant les vrais noms.
      sortFn: triSansGroupeEnDernier,
      cell: ({ row }) =>
        row.original.groupeNom ? (
          <span className="text-sm">{row.original.groupeNom}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
      meta: { libelle: "Groupe", largeur: "13%" },
    },
    {
      id: "templates",
      header: "Template",
      accessorFn: (c) => c.templates.join(", "),
      // Filtre « contient ce template », partagé avec le portail client.
      ...colonneListeFiltrable<CampagneOrgLigne>((c) => c.templates),
      cell: ({ row }) => <TemplatesCellule templates={row.original.templates} />,
      meta: { libelle: "Template", largeur: "13%" },
    },
    {
      id: "nbCandidats",
      header: "Candidats",
      accessorFn: (c) => c.nbCandidats,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="data">{row.original.nbCandidats}</span>
        </div>
      ),
      meta: { libelle: "Candidats", largeur: "10%" },
    },
    {
      id: "progression",
      header: "Progression",
      accessorFn: (c) => c.progression.pourcentage,
      cell: ({ row }) => {
        const p = row.original.progression;
        return (
          <div className="min-w-[100px]">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="data">{p.pourcentage}%</span>
              <span className="data text-muted-foreground">
                {p.terminees}/{p.total}
              </span>
            </div>
            <Progress value={p.pourcentage} className="h-2" />
          </div>
        );
      },
      meta: { libelle: "Progression", largeur: "15%" },
    },
    {
      id: "statut",
      header: "Statut",
      accessorFn: (c) => c.statut,
      // Alimente les segments de statut et leurs compteurs (facettes).
      filterFn: filtreFacette<CampagneOrgLigne>(),
      sortFn: triOrdonne<CampagneOrgLigne>(ORDRE_STATUT),
      cell: ({ row }) => <StatutCampagneBadge statut={row.original.statut} />,
      meta: { libelle: "Statut", largeur: "10%" },
    },
    {
      id: "dateCreation",
      header: "Créée le",
      accessorFn: (c) => c.dateCreation,
      cell: ({ row }) => (
        <span className="data text-sm">{dateCourte(row.original.dateCreation)}</span>
      ),
      meta: { libelle: "Créée le", largeur: "8%" },
    },
    {
      // Colonne porteuse de la facette « Archivage ». Masquée par défaut : elle
      // sert à filtrer, l'état lui-même se lit sur le badge de la colonne Nom.
      id: "archive",
      header: "Archivage",
      accessorFn: (c) =>
        c.archivedAt ? ETAT_ARCHIVE.ARCHIVEES : ETAT_ARCHIVE.ACTIVES,
      filterFn: filtreFacette<CampagneOrgLigne>(),
      cell: ({ row }) =>
        row.original.archivedAt ? (
          <span className="text-sm text-muted-foreground">
            {dateCourte(row.original.archivedAt)}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
      meta: {
        libelle: "Archivage",
        largeur: "8%",
        masqueeParDefaut: true,
      },
    },
    ...(actions
      ? [
          {
            id: "actions",
            header: "",
            enableSorting: false,
            enableHiding: false,
            cell: ({ row }: { row: { original: CampagneOrgLigne } }) => (
              <div className="flex items-center justify-end">
                {row.original.archivedAt ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Désarchiver ${row.original.nom}`}
                    title="Désarchiver"
                    onClick={(e) => {
                      // La ligne entière est un lien : sans ça, le clic
                      // naviguerait vers le détail de la campagne.
                      e.preventDefault();
                      e.stopPropagation();
                      actions.onDesarchiver(row.original);
                    }}
                  >
                    <ArchiveRestore className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Archiver ${row.original.nom}`}
                    title="Archiver"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      actions.onArchiver(row.original);
                    }}
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  aria-label={`Supprimer ${row.original.nom}`}
                  title="Supprimer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    actions.onSupprimer(row.original);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ),
            meta: { largeur: "9%", aDroite: true },
          } as ColonneDataTable<CampagneOrgLigne>,
        ]
      : []),
  ];
}
